'use client';

import { useEffect, useRef, useState } from 'react';

type Props = {
  phrases: string[];
  typeSpeed?: number;
  deleteSpeed?: number;
  holdMs?: number;
  gapMs?: number;
  startDelayMs?: number;
  className?: string;
};

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// Longest common prefix length between two phrases. When the next phrase
// shares its opening characters with the current one, we erase down to the
// shared prefix and resume typing from there — avoids the "stray leading I"
// stutter when five of six rotating phrases start with the letter I.
function commonPrefixLength(a: string, b: string): number {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a.charCodeAt(i) === b.charCodeAt(i)) i++;
  return i;
}

export function TypingText({
  phrases,
  typeSpeed = 50,
  deleteSpeed = 25,
  holdMs = 3200,
  gapMs = 300,
  startDelayMs = 0,
  className = '',
}: Props) {
  const [display, setDisplay] = useState(phrases[0] ?? '');
  const [reducedMotion, setReducedMotion] = useState(false);
  const [caretFaded, setCaretFaded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (reducedMotion || prefersReducedMotion()) {
      setDisplay(phrases[0] ?? '');
      return;
    }

    let cancelled = false;
    let phraseIdx = 0;
    // Start from the first phrase ALREADY fully shown — this matches the SSR
    // paint, so hydration no longer flashes blank-then-retype (the "half-typed
    // on load" glitch). We hold it, then delete + rotate from here.
    let current = phrases[0] ?? '';
    // When the next phrase shares a prefix with the current one, erasure
    // stops at this length instead of running all the way down to "".
    let targetEraseLen = 0;
    setDisplay(current);

    const clear = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };

    const schedule = (fn: () => void, ms: number) => {
      clear();
      timerRef.current = setTimeout(() => {
        if (!cancelled) fn();
      }, ms);
    };

    const typeNext = () => {
      const target = phrases[phraseIdx];
      if (current.length < target.length) {
        current = target.slice(0, current.length + 1);
        setDisplay(current);
        setCaretFaded(false);
        schedule(typeNext, typeSpeed);
      } else {
        // Phrase complete. Decide how far the upcoming erasure should run.
        const nextPhrase = phrases[(phraseIdx + 1) % phrases.length];
        const shared = commonPrefixLength(target, nextPhrase);
        targetEraseLen = shared >= 2 ? shared : 0;
        // Let the caret blink for ~1.4s, then fade it out over 1.8s before
        // the delete begins.
        if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = setTimeout(() => {
          if (!cancelled) setCaretFaded(true);
        }, Math.max(0, holdMs - 1800));
        schedule(deleteNext, holdMs);
      }
    };

    const deleteNext = () => {
      setCaretFaded(true);
      if (current.length > targetEraseLen) {
        current = current.slice(0, -1);
        setDisplay(current);
        schedule(deleteNext, deleteSpeed);
      } else {
        phraseIdx = (phraseIdx + 1) % phrases.length;
        setCaretFaded(false);
        // When the shared prefix is reused, the transition feels like one
        // continuous edit — skip most of the gap so it doesn't read as a
        // pause-then-restart.
        const reusedPrefix = targetEraseLen > 0;
        targetEraseLen = 0;
        // Empty-state reset gets a longer dwell so it reads as deliberate
        // — without this it felt twitchy vs. the prefix-preserving gap.
        schedule(typeNext, reusedPrefix ? Math.min(120, gapMs) : Math.max(550, gapMs));
      }
    };

    // Kick off from the "phrase 0 complete" state: hold the fully-shown first
    // phrase through the start delay, then fade the caret and begin deleting.
    const firstNext = phrases[1 % phrases.length] ?? current;
    const firstShared = commonPrefixLength(current, firstNext);
    targetEraseLen = firstShared >= 2 ? firstShared : 0;
    const firstHold = startDelayMs + holdMs;
    fadeTimerRef.current = setTimeout(() => {
      if (!cancelled) setCaretFaded(true);
    }, Math.max(0, firstHold - 1800));
    schedule(deleteNext, firstHold);

    return () => {
      cancelled = true;
      clear();
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, [phrases, typeSpeed, deleteSpeed, holdMs, gapMs, startDelayMs, reducedMotion]);

  return (
    <span data-typing-text className={className}>
      <span>{display}</span>
      {!reducedMotion && (
        <span
          aria-hidden="true"
          className="inline-block align-[-0.1em] ml-[2px]"
          style={{
            width: 2,
            height: '1em',
            background: 'var(--accent)',
            animation: caretFaded ? 'none' : 'blink 1s step-end infinite',
            opacity: caretFaded ? 0 : 1,
            transition: 'opacity 1.8s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        />
      )}
    </span>
  );
}
